import { useDocumentTitle } from 'usehooks-ts';

import CircularFlowsDetectedBanner from 'components/banners/CircularDiagramDetectedBanner';
import InvalidAmountsBanner from 'components/banners/InvalidAmountsBanner';
import InvalidComparisonAmountsBanner from 'components/banners/InvalidComparisonAmountsBanner';
import { useGlobalModalsContext } from 'contexts/globalModalsContext';
import { Box, Flex } from 'ui/Layout';
import Skeleton from 'ui/Skeleton';

import ActionMenu from '../../components/ActionMenu';
import ErrorPage from '../../components/ErrorPage';
import Page from '../../components/Page';
import PageFooter from '../../components/PageFooter';
import SankeyBreadcrumb from '../../components/SankeyBreadcrumb';
import SankeyInteractionProvider from '../../components/SankeyInteractionProvider';
import SankeyStateHistoryProvider from '../../components/SankeyStateHistoryProvider';
import TopNavBar from '../../components/TopNavBar';
import AdminNotOfficialEditorBanner from '../../components/banners/AdminNotOfficialEditorBanner';
import CustomerExceededSankeysLimitBanner from '../../components/banners/CustomerExceededSankeysLimitBanner';
import NonCustomerExceededSankeysLimitBanner from '../../components/banners/NonCustomerExceededSankeysLimitBanner';
import { useAppEnvironmentContext } from '../../contexts/appEnvironmentContext';
import { useMediaQueryContext } from '../../contexts/mediaQueryContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { useSankeyFileName } from '../../diagram/title';
import { useConditionalLeavePagePrompt } from '../../utils/form';
import EditorDesktopLayout from './EditorDesktopLayout';
import EditorMobileLayout from './EditorMobileLayout';
import ReadOnlyLayout from './ReadOnlyLayout';

const SankeyPage: React.FC = () => {
  const { appEnvironment } = useAppEnvironmentContext();
  const { isDesktopScreen } = useMediaQueryContext();
  const { isAuthOrConversionModalOpen } = useGlobalModalsContext();
  const { isReadOnly, isErrorFetching, isLoading } = useSankeyFormContext();
  const fileName = useSankeyFileName();

  useDocumentTitle(`${fileName} · Sankey editor`);

  // Warn the user if they try to leave the page without saving to make sure they don't lose their work
  useConditionalLeavePagePrompt();

  return (
    <SankeyStateHistoryProvider>
      <SankeyInteractionProvider>
        <Page>
          <TopNavBar>
            <SankeyBreadcrumb />
          </TopNavBar>
          <ActionMenu hasPresentButton={appEnvironment === 'office'} />
          <Box
            flexGrow={1}
            maxW="var(--chakra-breakpoints-xxl)"
            mx="auto"
            p={3}
            w="100%"
          >
            <Flex direction="column">
              <AdminNotOfficialEditorBanner />
              <NonCustomerExceededSankeysLimitBanner />
              <CustomerExceededSankeysLimitBanner />
              <InvalidAmountsBanner />
              <InvalidComparisonAmountsBanner />
              <CircularFlowsDetectedBanner />
            </Flex>
            {isLoading ? (
              <Skeleton.Row w="100%" h="100%" minH="200px" />
            ) : isErrorFetching ? (
              <ErrorPage title="The diagram you are looking for is not here" />
            ) : isReadOnly && !isAuthOrConversionModalOpen ? (
              <ReadOnlyLayout />
            ) : isDesktopScreen ? (
              <EditorDesktopLayout />
            ) : (
              <EditorMobileLayout />
            )}
          </Box>
          <PageFooter />
        </Page>
      </SankeyInteractionProvider>
    </SankeyStateHistoryProvider>
  );
};

export default SankeyPage;
